import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { toast } from "react-toastify";
import { getMySurveyResponseDetails, getMySurveyResponses } from "../../service/mySurvey";
import { SurveyService } from "../../service/saveSurvey";
import {
  buildResponseParams, getResultData, parseResponsePage,
  parseSubmissionDate, responseIdForDeletion,
} from "../../utils/surveyResults";

const nameOf = (row) => row.respondent_name || row.name || `Member ${row.member_id ?? ""}`;
const messageOf = (error) => error?.response?.data?.message || error?.message || "Unable to load survey responses.";
const answerText = (value) => {
  if (Array.isArray(value)) return value.map((item) => item?.option_text || item?.text || String(item)).join(", ");
  if (value && typeof value === "object") {
    const parts = [];
    if (value.answer) parts.push(String(value.answer));
    if (Array.isArray(value.selected_options)) parts.push(answerText(value.selected_options));
    return parts.join(" · ") || JSON.stringify(value);
  }
  return value == null || value === "" ? "—" : String(value);
};

const FilterMenu = ({ label, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (event) => { if (!ref.current?.contains(event.target)) setOpen(false); };
    const escape = (event) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, []);
  return <div className="survey-filter-wrap" ref={ref}>
    <button type="button" className="survey-filter-button" aria-expanded={open} onClick={() => setOpen(!open)}>
      <span>{label}: {value}</span><ChevronDown size={12} />
    </button>
    {open && <div className="survey-filter-menu">{options.map((option) => (
      <button key={option} type="button" className={value === option ? "selected" : ""} onClick={() => { onChange(option); setOpen(false); }}>{option}</button>
    ))}</div>}
  </div>;
};

const SubmissionDate = ({ value }) => {
  const date = parseSubmissionDate(value);
  return date ? <>{date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}<span>{date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}</span></> : <>{value || "—"}</>;
};

export default function SurveyResponses({ surveyId, onRowsChange, onDeleted }) {
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [device, setDevice] = useState("All");
  const [date, setDate] = useState("Last 7 Days");
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [data, setData] = useState(() => parseResponsePage());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [responseDetails, setResponseDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const requestId = useRef(0);
  const closeRef = useRef(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const id = ++requestId.current;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    if (search !== query) return () => controller.abort();
    getMySurveyResponses(surveyId, buildResponseParams({ page, search, status, device, date }), { signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted || id !== requestId.current) return;
        const result = parseResponsePage(getResultData(response));
        if (page > result.pages) { setPage(result.pages); return; }
        setData(result);
      })
      .catch((requestError) => {
        if (controller.signal.aborted || id !== requestId.current) return;
        setData(parseResponsePage());
        setError(messageOf(requestError));
      })
      .finally(() => { if (!controller.signal.aborted && id === requestId.current) setLoading(false); });
    return () => controller.abort();
  }, [surveyId, page, search, query, status, device, date, refresh]);

  useEffect(() => {
    onRowsChange(loading || error ? [] : data.rows);
    return () => onRowsChange([]);
  }, [data.rows, loading, error, onRowsChange]);

  useEffect(() => {
    if (!selected) return;
    const previous = document.activeElement;
    closeRef.current?.focus();
    const handleKey = (event) => {
      if (event.key === "Escape" && !deleting) setSelected(null);
    };
    document.addEventListener("keydown", handleKey);
    return () => { document.removeEventListener("keydown", handleKey); previous?.focus(); };
  }, [selected, deleting]);

  useEffect(() => {
    if (!selected?.participant_id) return;
    let active = true;
    setResponseDetails(null);
    setDetailsError("");
    setDetailsLoading(true);
    getMySurveyResponseDetails(surveyId, selected.participant_id)
      .then((response) => {
        if (active) setResponseDetails(getResultData(response));
      })
      .catch((requestError) => {
        if (active) setDetailsError(messageOf(requestError));
      })
      .finally(() => { if (active) setDetailsLoading(false); });
    return () => { active = false; };
  }, [surveyId, selected]);

  const change = (setter) => (value) => { setter(value); setPage(1); };
  const openResponse = (row) => { setSelected(row); setResponseDetails(null); setConfirmDelete(false); setDeleteError(""); };
  const close = () => { if (!deleting) { setSelected(null); setResponseDetails(null); } };
  const remove = async () => {
    const responseId = responseIdForDeletion(selected);
    if (!responseId || deleting) return;
    setDeleting(true);
    setDeleteError("");
    try {
      const response = await SurveyService.deleteSurveyResponse(responseId);
      if (!response?.data || response.data.success === false) throw new Error(response?.data?.message || "Unable to delete this response.");
      toast.success(response.data.message || "Survey response deleted.");
      setSelected(null);
      setRefresh((value) => value + 1);
      onDeleted();
    } catch (requestError) {
      setDeleteError(messageOf(requestError));
    } finally {
      setDeleting(false);
    }
  };
  const first = data.total ? (data.page - 1) * data.limit + 1 : 0;
  const startPage = Math.max(1, Math.min(page - 2, data.pages - 4));

  return <div className="responses-panel">
    <h2>Individual Responses</h2>
    <p className="responses-subtitle">{loading ? "Loading responses…" : `${data.total.toLocaleString()} matching respondents · completed and partial submissions`}</p>
    <div className="responses-toolbar">
      <label className="responses-search"><Search size={15} /><input value={query} onChange={(event) => change(setQuery)(event.target.value)} placeholder="Search name or member ID..." aria-label="Search responses" /></label>
      <FilterMenu label="Status" value={status} options={["All", "Completed", "Partial"]} onChange={change(setStatus)} />
      <FilterMenu label="Device" value={device} options={["All", "Desktop", "Mobile", "Tablet", "Unknown"]} onChange={change(setDevice)} />
      <FilterMenu label="Date" value={date} options={["Last 7 Days", "Last 30 Days", "Last 90 Days", "All Time"]} onChange={change(setDate)} />
    </div>
    {error && <div className="alert alert-danger py-2 ft-12" role="alert">{error} <button type="button" className="response-view" onClick={() => setRefresh((value) => value + 1)}>Try again</button></div>}
    <div className="responses-table-wrap" aria-busy={loading}>
      <table className="responses-table"><thead><tr><th>Respondent</th><th>Submitted</th><th>Time Taken</th><th>Device</th><th>Status</th><th aria-label="Action" /></tr></thead>
        <tbody>{!loading && !error && data.rows.length ? data.rows.map((row, index) => (
          <tr key={row.participant_id ?? row.response_id ?? index}>
            <td><span className="respondent-avatar" style={{ background: "#7ca9a7" }}>{nameOf(row).charAt(0).toUpperCase()}</span><strong>{nameOf(row)}</strong></td>
            <td><SubmissionDate value={row.submitted_at} /></td><td>{row.time_taken || "—"}</td><td>{row.device || "Unknown"}</td>
            <td><span className={`response-status ${String(row.status).toLowerCase() === "completed" ? "completed" : "partial"}`}>{row.status || "Partial"}</span></td>
            <td><button className="response-view" type="button" onClick={() => openResponse(row)} aria-label={`View response from ${nameOf(row)}`}>View →</button></td>
          </tr>
        )) : <tr><td colSpan="6" className="responses-no-data">{loading ? "Loading responses…" : error ? "Responses could not be loaded." : "No responses match these filters."}</td></tr>}</tbody>
      </table>
    </div>
    {!loading && !error && data.total > 0 && <div className="responses-pagination"><span>Showing {first}–{first + data.rows.length - 1} of {data.total.toLocaleString()} responses</span><div>
      <button type="button" aria-label="Previous responses page" disabled={page === 1} onClick={() => setPage(page - 1)}>←</button>
      {Array.from({ length: Math.min(data.pages, 5) }, (_, index) => startPage + index).map((value) => <button type="button" key={value} aria-label={`Responses page ${value}`} aria-current={page === value ? "page" : undefined} className={page === value ? "active" : ""} onClick={() => setPage(value)}>{value}</button>)}
      <button type="button" aria-label="Next responses page" disabled={page === data.pages} onClick={() => setPage(page + 1)}>→</button>
    </div></div>}
    {selected && <div className="response-modal-backdrop" onClick={close}><div className="response-modal" role="dialog" aria-modal="true" aria-labelledby="response-title" onClick={(event) => event.stopPropagation()}>
      <button ref={closeRef} className="response-modal-close" disabled={deleting} onClick={close} aria-label="Close response details">×</button>
      <h2 id="response-title">{nameOf(selected)}</h2><p>{selected.status} · Member {selected.member_id ?? "—"}</p>
      <dl><dt>Submitted</dt><dd>{selected.submitted_at || "—"}</dd><dt>Time taken</dt><dd>{selected.time_taken || "—"}</dd><dt>Device</dt><dd>{selected.device || "Unknown"}</dd></dl>
      {detailsLoading && <p role="status">Loading answers…</p>}
      {detailsError && <p className="text-danger" role="alert">{detailsError}</p>}
      {Object.entries(responseDetails?.answers || selected.answers || selected.response_data || {}).map(([key, value]) => <div key={key}><strong>{value?.question_text || key}</strong><p>{answerText(value)}</p></div>)}
      {deleteError && <p className="text-danger" role="alert">{deleteError}</p>}
      {responseIdForDeletion(selected) ? confirmDelete ? <div role="alert"><p>Delete this submitted response? This cannot be undone.</p><button type="button" className="survey-export-button" disabled={deleting} onClick={remove}>{deleting ? "Deleting…" : "Confirm deletion"}</button><button type="button" className="response-view mt-2" disabled={deleting} onClick={() => setConfirmDelete(false)}>Cancel</button></div> : <button type="button" className="response-view" onClick={() => setConfirmDelete(true)}>Delete response</button> : <p>This response cannot be deleted here because its response ID is unavailable.</p>}
    </div></div>}
  </div>;
}
